import { useState } from 'react';
import { perfumesCAB, perfumesDAMA, perfumesUNISEX, CategorizedPerfume } from '../../../mocks/products';

const WA_BASE = 'https://wa.me/573228000501?text=Hola%2C%20me%20interesa%20el%20perfume%20';
const WA_CATALOG = 'https://wa.me/573228000501?text=Hola%2C%20me%20gustar%C3%ADa%20conocer%20m%C3%A1s%20fragancias%20disponibles%20%F0%9F%92%9B';

const INITIAL_VISIBLE = 8;

type CategoryKey = 'caballero' | 'dama' | 'unisex';

const CATEGORIES: { key: CategoryKey; label: string; icon: string; data: CategorizedPerfume[] }[] = [
  { key: 'caballero', label: 'Caballero', icon: 'ri-men-fill', data: perfumesCAB },
  { key: 'dama', label: 'Dama', icon: 'ri-women-fill', data: perfumesDAMA },
  { key: 'unisex', label: 'Unisex', icon: 'ri-user-2-fill', data: perfumesUNISEX },
];

function PerfumeCard({ product }: { product: CategorizedPerfume }) {
  return (
    <article className="bg-white border border-stone-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 group flex flex-col">
      <div className="relative aspect-[4/5] overflow-hidden bg-stone-50">
        <img
          src={product.image}
          alt={product.name}
          title={`${product.name} - Perfume original Laura Soto Shop`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {product.isBestSeller && (
          <div className="absolute top-3 left-3 bg-amber-500 text-white text-[10px] font-sans font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
            Más vendido
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-serif font-semibold text-sm md:text-[15px] text-stone-900 mb-1 leading-snug">
          {product.name}
        </h3>
        <p className="font-sans text-xs text-stone-500 mb-3 line-clamp-2 flex-1 leading-relaxed">
          {product.description}
        </p>
        <div className="mb-3">
          {product.originalPrice ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-sans text-sm text-stone-400 line-through">{product.originalPrice}</span>
              <span className="font-sans font-bold text-base md:text-lg text-red-600">{product.price}</span>
              {product.discount && (
                <span className="font-sans text-[10px] font-bold text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  {product.discount}
                </span>
              )}
            </div>
          ) : (
            <p className="font-sans font-bold text-base md:text-lg text-stone-900">{product.price}</p>
          )}
        </div>
        <a
          href={`${WA_BASE}${encodeURIComponent(product.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-2.5 px-4 rounded-xl font-sans text-xs font-semibold transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-whatsapp-fill text-sm" />
          Consultar por WhatsApp
        </a>
      </div>
    </article>
  );
}

export default function PerfumeCollection() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>('caballero');
  const [showAllMap, setShowAllMap] = useState<Record<CategoryKey, boolean>>({
    caballero: false,
    dama: false,
    unisex: false,
  });

  const currentCat = CATEGORIES.find((c) => c.key === activeCategory)!;
  const isShowAll = showAllMap[activeCategory];
  const visibleProducts = isShowAll
    ? currentCat.data
    : currentCat.data.slice(0, INITIAL_VISIBLE);
  const remaining = currentCat.data.length - INITIAL_VISIBLE;

  const handleToggleShowAll = () => {
    if (isShowAll) {
      setShowAllMap((prev) => ({ ...prev, [activeCategory]: false }));
      document.getElementById('perfumes')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setShowAllMap((prev) => ({ ...prev, [activeCategory]: true }));
    }
  };

  const handleCategoryChange = (key: CategoryKey) => {
    setActiveCategory(key);
  };

  return (
    <section id="perfumes" className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block font-sans text-xs tracking-[0.25em] text-amber-600 uppercase mb-3">
            Fragancias Exclusivas
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            Nuestra Colección de Perfumes
          </h2>
          <div className="w-10 h-0.5 bg-amber-500 mx-auto mb-4" />
          <p className="font-sans text-sm md:text-base text-stone-500">
            Descubre fragancias únicas — 100% originales, selladas de fábrica
          </p>
        </div>

        {/* Sello Garantía */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10 py-5 px-6 bg-amber-50 border border-amber-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 bg-amber-100 rounded-full border border-amber-300">
              <i className="ri-shield-check-fill text-amber-600 text-xl" />
            </div>
            <div className="text-left">
              <p className="font-sans text-sm font-bold text-stone-800">100% Originales</p>
              <p className="font-sans text-xs text-stone-500">Sellados de fábrica, garantizados</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-amber-200" />
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 bg-amber-100 rounded-full border border-amber-300">
              <i className="ri-award-fill text-amber-600 text-xl" />
            </div>
            <div className="text-left">
              <p className="font-sans text-sm font-bold text-stone-800">Marcas Auténticas</p>
              <p className="font-sans text-xs text-stone-500">Nunca vendemos imitaciones</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-8 bg-amber-200" />
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 bg-amber-100 rounded-full border border-amber-300">
              <i className="ri-whatsapp-fill text-amber-600 text-xl" />
            </div>
            <div className="text-left">
              <p className="font-sans text-sm font-bold text-stone-800">Envíos a Todo el País</p>
              <p className="font-sans text-xs text-stone-500">Consulta disponibilidad por WhatsApp</p>
            </div>
          </div>
        </div>

        {/* Tabs de Categorías */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => handleCategoryChange(cat.key)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-sans text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.key
                  ? 'bg-stone-900 text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-sm`} />
              </div>
              {cat.label}
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full font-sans ${
                  activeCategory === cat.key
                    ? 'bg-white/20 text-white'
                    : 'bg-stone-300 text-stone-600'
                }`}
              >
                {cat.data.length}
              </span>
            </button>
          ))}
        </div>

        {/* Promo Banner */}
        <div className="mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-stone-50 border border-stone-200 rounded-2xl px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
              <i className="ri-search-2-line text-amber-600 text-base" />
            </div>
            <p className="font-sans text-sm text-stone-700 leading-snug">
              ¿No encuentras la fragancia que buscas?{' '}
              <span className="text-amber-600 font-semibold">Consúltanos por WhatsApp</span>
              {' '}— tenemos muchas más opciones disponibles.
            </p>
          </div>
          <a
            href={WA_CATALOG}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-sans text-xs font-semibold px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-fill text-sm" />
            Consultar ahora
          </a>
        </div>

        {/* Grid */}
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
          data-product-shop={true}
        >
          {visibleProducts.map((p) => (
            <PerfumeCard key={`${activeCategory}-${p.id}`} product={p} />
          ))}
        </div>

        {/* CTA Ver más / Ver menos */}
        <div className="text-center mt-10">
          {!isShowAll ? (
            remaining > 0 ? (
              <>
                <button
                  onClick={handleToggleShowAll}
                  className="inline-flex items-center gap-3 bg-stone-900 hover:bg-amber-500 text-white font-sans text-sm font-semibold px-10 py-4 rounded-full transition-all duration-300 cursor-pointer hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-arrow-down-s-line text-lg" />
                  Ver más {currentCat.label.toLowerCase()} ({remaining} más)
                </button>
                <p className="font-sans text-xs text-stone-400 mt-3">
                  Mostrando {INITIAL_VISIBLE} de {currentCat.data.length} fragancias
                </p>
              </>
            ) : null
          ) : (
            <>
              <button
                onClick={handleToggleShowAll}
                className="inline-flex items-center gap-3 bg-amber-500 hover:bg-stone-900 text-white font-sans text-sm font-semibold px-10 py-4 rounded-full transition-all duration-300 cursor-pointer hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-arrow-up-s-line text-lg" />
                Ver menos
              </button>
              <p className="font-sans text-xs text-stone-400 mt-3">
                Mostrando las {currentCat.data.length} fragancias de {currentCat.label.toLowerCase()}
              </p>
            </>
          )}
        </div>

        {/* Banner Final */}
        <div className="mt-14 bg-stone-900 rounded-2xl px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <p className="font-serif text-xl md:text-2xl font-bold text-white mb-2">
              ¿No viste la fragancia que buscabas?
            </p>
            <p className="font-sans text-sm text-stone-400 leading-relaxed">
              Tenemos muchas más disponibles. Escríbenos por WhatsApp y te ayudamos a encontrarla.
            </p>
          </div>
          <a
            href={WA_CATALOG}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-sans text-sm font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-fill text-xl" />
            Escríbenos por WhatsApp
          </a>
        </div>

      </div>
    </section>
  );
}
